import { createStyles, makeStyles, Theme } from '@material-ui/core';

import {
  typography12MediumStyle,
  typography12RegularStyle,
  typography15RegularStyle,
} from 'src/legacy/components/Text/BaseTypography';
import {
  BlackHeadings,
  DividersAndCardBorders,
  GraySmall,
  HoverBackground,
  WebLink,
  white,
} from 'src/theme/colors';
import { RegularInputShadow } from 'src/theme/shadows';
import { zIndex } from 'src/theme/zindex';

type StyleProps = {
  showMessagingSidebar: boolean;
  isSidebarTheme: boolean;
};
export const useMessagesStyles = makeStyles<Theme, StyleProps>((theme) =>
  createStyles({
    root: {
      height: '100%',
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
      '& .portal-chat': {
        backgroundColor: 'inherit',
        containerType: 'inline-size',
        '&.str-chat': {
          border: 'none',
          '& *': {
            fontFamily: theme.typography.fontFamily + '!important',
          },
          flex: 1,
          maxHeight: '100%',
          [theme.breakpoints.up('sm')]: {
            overflow: 'hidden',
          },
        },

        '& .str-chat__list .str-chat__message-list-scroll ': {
          padding: '12px 0 0 0',
          [theme.breakpoints.down('xs')]: {
            paddingBottom: theme.spacing(6),
          },
        },
        '& .str-chat__list .str-chat__message-list-scroll .str-chat__li ': {
          marginInline: 0,
          paddingInline: 0,
        },
        '& .str-chat__li': { position: 'relative' },
        '& .str-chat__attachment-list .str-chat__message-attachment-card--header':
          {
            [theme.breakpoints.up('sm')]: {
              maxHeight: ({ isSidebarTheme }) =>
                isSidebarTheme ? '150px' : '200px',
            },
            [theme.breakpoints.down('xs')]: {
              maxHeight: '150px',
            },
          },
        '& .str-chat__attachment-list .str-chat__message-attachment-card--header img':
          {
            maxHeight: '100% ', // Remove max-height constraint
          },
        '& .str-chat__loading-channels': {
          width: '100%',
          backgroundColor: '#fafafa',
          padding: theme.spacing(2.5),
          [theme.breakpoints.down('xs')]: {
            width: '100%',
          },
        },
        '& .str-chat__loading-channels-avatar, .str-chat__loading-channels-status, .str-chat__loading-channels-username':
          {
            backgroundImage:
              '-webkit-gradient(linear, right top, left top, from(#F2F2F2), to(#E5E5E5))',
            borderRadius: theme.shape.borderRadius,
          },
        '&.str-chat-channel-list': {
          boxShadow: 'unset',
          float: 'none',
          overflowX: 'hidden',
        },
        '& .str-chat__list': {
          backgroundColor: 'inherit',
          padding: 0,
          [theme.breakpoints.up('sm')]: {
            overflowY: 'auto',
          },
          [theme.breakpoints.down('xs')]: {
            overflowY: 'scroll',
            overflowX: 'hidden',
          },
          '&.str-chat__list--thread': {
            wordBreak: 'break-word',
          },
          '& .str-chat__reverse-infinite-scroll': {
            paddingTop: theme.spacing(1.5), // leave space for first message actions menu
            [theme.breakpoints.down('xs')]: {
              paddingBottom: theme.spacing(5),
            },
          },
        },
        '& .str-chat__li--top, .str-chat__li--single': {
          marginTop: 0,
          padding: 0,
          '& > div': {
            paddingTop: 12,
          },
        },
        '& .str-chat__channel-list-messenger': {
          height: '100%',
          paddingBottom: '0px !important',
        },
        '& .str-chat__channel-list-messenger__main': {
          padding: 0,
          background: '#fff',
          height: '100%',
          // TODO: support scrollbar in channels sidebar
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          [theme.breakpoints.up('sm')]: {
            '@supports ( -moz-appearance:none )': {
              scrollbarColor: 'gray',
              scrollbarWidth: 'thin',
            },
          },
          [theme.breakpoints.down('xs')]: {
            width: '100%',
          },
        },
        '& .str-chat__message-team-actions': {
          display: 'none',
        },
        '& .str-chat__input-flat-emojiselect': {
          display: 'none',
        },
        '& .str-chat__input-flat .str-chat__textarea > textarea': {
          borderRadius: 4,
          border: `1px solid ${DividersAndCardBorders}`,
          boxShadow: RegularInputShadow,
          backgroundColor: '#fff',
          [theme.breakpoints.down('xs')]: {
            fontSize: '1rem',
            overflowY: 'hidden',
            boxShadow: 'none',
            border: 'none',
            borderTop: `1px solid ${DividersAndCardBorders}`,
            borderRadius: 0,
          },
        },
        '& .str-chat__message-team-status': {
          display: 'none',
        },
        '& .str-chat__input-flat': {
          [theme.breakpoints.down('xs')]: {
            padding: '0 5px',
          },
          [theme.breakpoints.up('sm')]: {
            padding: (props) =>
              props.showMessagingSidebar
                ? theme.spacing(1, 4.5, 1, 2)
                : theme.spacing(1),
          },
        },
        '& .str-chat__container': {
          position: 'relative',
          [theme.breakpoints.down('xs')]: {
            marginTop: 0,
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
        '& .str-chat__li--bottom, .str-chat__li--single': {
          '& > div': {
            paddingBottom: 12,
          },
        },
        '& .str-chat__message-attachment': {
          borderRadius: 0,
          justifyContent: 'flex-start',
          maxWidth: ({ isSidebarTheme }) => (isSidebarTheme ? 300 : 400),
          width: '100%',
        },
        '& .str-chat__message-attachment--gallery': {
          width: 'auto',
        },
        '& .str-chat__message-attachment-card': {
          width: '100%',
          minHeight: 'unset',
        },
        '& .str-chat__message-attachment--card--no-image': {
          height: 'auto',
        },
        '& .str-chat__message-attachment--image:not(.str-chat__message-attachment--card)':
          {
            borderRadius: '8px',
            [theme.breakpoints.up('sm')]: {
              maxHeight: ({ isSidebarTheme }) =>
                isSidebarTheme ? '200px' : '400px',
            },
            [theme.breakpoints.down('xs')]: {
              maxHeight: '200px',
            },
          },
        '& .str-chat__message-attachment--img': {
          borderRadius: 0,
          border: 'none',
          paddingLeft: 0,
          width: '100%',
          maxWidth: '400px',
        },
        '& .str-chat__message-attachment-file--item': {
          height: '60px',
          border: 'none',
          padding: 0,
          backgroundColor: ({ isSidebarTheme }) =>
            isSidebarTheme ? HoverBackground : white,
        },
        // that fix images position within image gallery previewer
        '& .str-chat__gallery': {
          justifyContent: 'flex-start',
        },
        '& .str-chat__message-input .str-chat__message-input-inner .str-chat__message-textarea-container .str-chat__message-textarea-with-emoji-picker ':
          {
            padding: '8px 12px 9px 15px',
            [theme.breakpoints.down('xs')]: {
              paddingBottom: '9.5px',
              paddingLeft: '13.8px',
            },
          },
        '& .str-chat__message-team': {
          position: 'relative',
          paddingLeft: 0,
          [theme.breakpoints.down('xs')]: {
            padding: '0px 40px 0px 0px',
          },
          [theme.breakpoints.up('sm')]: {
            paddingRight: ({ isSidebarTheme }) =>
              isSidebarTheme ? '16px' : '120px', // avoid message long text overlap with read recipients when not internal chat
          },
          '& .str-chat__message-team-meta': {
            paddingRight: 0,
            marginTop: 5,
          },
          '&:hover .str-chat__message-team-content': {
            background: 'transparent',
          },
          '& .str-chat__message-team-content': {
            marginTop: theme.spacing(0.5),
            marginBottom: theme.spacing(1),
            paddingLeft: 0,
            borderLeft: 'none',
            '& ul': {
              '& li': {
                fontSize: '15px',
                fontWeight: 400,
                lineHeight: '24px',
              },
            },
            '& ol': {
              '& li': {
                fontSize: '15px',
                fontWeight: 400,
                lineHeight: '24px',
              },
            },
            '& .str-chat__message-url-link': {
              wordBreak: 'break-word',
              verticalAlign: 'bottom',
              color: WebLink,
              ...typography15RegularStyle,
            },
            '& p': {
              margin: 0, // Remove the default margin applied by Stream Chat to individual messages
              whiteSpace: 'pre-wrap', // Ensure that line breaks are preserved
            },
            // The above code removes the default margin applied by Stream Chat to individual messages.
            // The following code matches the <br> tag height with the margin between message paragraphs.
            // This ensures that the <br> tag doesn't create a large gap between messages.
            '& br': {
              lineHeight: '16px',
            },
            [theme.breakpoints.down('xs')]: {
              maxWidth: '100%',
            },

            '& pre': {
              fontSize: '12px',
              fontWeight: 700,
              backgroundColor: '#f8f8f8',
              borderRadius: 4,
              border: `1px solid #d3d3d3`,
              padding: '10px',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              margin: '0 5px 0 0',
              lineHeight: '22px',
            },
          },
          '& .str-chat__attachment-list .str-chat__message-attachment-card-react--header img':
            {
              cursor: 'pointer',
              touchAction: 'none',
              pointerEvents: 'none',
            },

          '& .str-chat__message-attachment-card--text': {
            display: 'block',
            color: GraySmall,
            ...typography12RegularStyle,
          },
        },
        '& .str-chat__message-simple-status': {
          left: 0,
          bottom: 0,
          right: theme.spacing(4.5),
          [theme.breakpoints.down('xs')]: {
            right: 0,
          },
        },
        '& .rfu-dropzone': {
          outline: 'none',
        },
        '& .str-chat__input-flat-wrapper': {
          [theme.breakpoints.down('xs')]: {
            backgroundColor: '#fff',
            position: 'fixed',
            bottom: '0',
            left: 0,
            right: 0,
          },
        },
        '& .str-chat__send-button': {
          display: 'none',
        },
        '& .str-chat__input-flat .rfu-file-upload-button': {
          color: theme.palette.primary.main,
          position: 'absolute',
          left: '25px',
          transform: 'translate(-10px,-10px)',
          borderRight: `1px solid ${DividersAndCardBorders}`,
          width: '40px',
          height: '35px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingRight: '5px',
          '& svg': {
            opacity: 1,
            fontSize: '20px',
          },
        },
        '& .str-chat__input-flat--textarea-wrapper .str-chat__fileupload-wrapper .str-chat__tooltip':
          {
            display: 'none',
          },
        '& .image-gallery-content.fullscreen .image-gallery-slide .image-gallery-image':
          {
            height: '100%',
          },
      },
      '& .str-chat__message-attachment-card--title': {
        color: BlackHeadings,
        ...typography12MediumStyle,
      },
      '& .str-chat__message-attachment-card--url': {
        color: BlackHeadings,
        ...typography12MediumStyle,
        textTransform: 'lowercase',
        opacity: 1,
      },
      '& .str-chat__message-team .str-chat__message-attachment-card--content': {
        background: white,
      },
      '& .str-chat__message-team .str-chat__message-attachment-card': {
        background: white,
        border: `1px solid ${DividersAndCardBorders}`,
        borderRadius: 4,
      },
      '& .str-chat__message-attachment--media': {
        maxWidth: '400px !important',
        width: '100%',
      },
      '&  .str-chat__message-attachment--image .str-chat__modal--open .str-chat__modal__inner.str-chat-react__modal__inner':
        {
          padding: theme.spacing(1),
        },
      '& .str-chat__modal--open': {
        zIndex: 99999,
      },
      '& .str-chat__ul': { margin: 0, listStyle: 'none' },
      '& .str-chat__message-team-content p': {
        color: BlackHeadings,
        ...typography15RegularStyle,

        '& code': {
          fontSize: '12px',
          fontWeight: 400,
          backgroundColor: '#f8f8f8',
          borderRadius: 3,
          border: `1px solid #d0021b1a`,
          padding: '2px',
          wordBreak: 'break-all',
          whiteSpace: 'pre-wrap',
        },
      },

      '& .str-chat__message-simple-status': {
        margin: '8px 0 8px 8px',
        order: 3,
        position: 'absolute',
        bottom: 0,
        lineHeight: 1,
        width: 'fit-content',
        textAlign: 'right',
        marginLeft: 'auto',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        [theme.breakpoints.down('xs')]: {
          right: 0,
        },
        zIndex: 1,
      },
      '& .str-chat__message-input .str-chat__message-textarea-container .str-chat__message-textarea':
        { fontSize: '1rem', lineHeight: '17px' },
      '& .str-chat__message-input .str-chat__message-input-inner': {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        borderRadius: 4,
        minHeight: '59px',
        marginBottom: 4,

        padding: '8px 12px 10px 14px',
        border: `1px solid ${DividersAndCardBorders}`,
        boxShadow: RegularInputShadow,
        backgroundColor: '#fff',

        [theme.breakpoints.down('xs')]: {
          minHeight: '58px',
          fontSize: '1rem',
          overflowY: 'hidden',
          boxShadow: 'none',
          border: 'none',
          borderTop: `1px solid ${DividersAndCardBorders}`,
          borderRadius: 0,
          paddingLeft: theme.spacing(2),
          paddingBottom: '9.5px',
        },
      },
      '& .str-chat__message-input .str-chat__message-textarea-container': {
        border: 'none',
      },
      '& .str-chat__message-input': {
        [theme.breakpoints.up('sm')]: {
          padding: (props) =>
            props.showMessagingSidebar
              ? theme.spacing(1, 4.5, 1, 2)
              : theme.spacing(1, 1, 0.85, 1),
        },

        [theme.breakpoints.down('xs')]: {
          padding: 0,
          backgroundColor: '#fff',
          position: 'fixed',
          bottom: '0',
          left: 0,
          right: 0,
          // Ensure the message input stays above messages and the scrollbar (prevents overlap)
          zIndex: zIndex.channelsListMobileOverlay,
        },
      },
      '& .str-chat__attachment-list .str-chat__message-attachment--card': {
        borderRadius: 4,
        '& .str-chat__message-attachment-card--text': {
          whiteSpace: 'wrap',
          fontSize: '12px',
        },
        '& .str-chat__message-attachment-card--content .str-chat__message-attachment-card--title':
          {
            whiteSpace: 'wrap',
            fontSize: '12px',
          },
      },
      '& .str-chat__attachment-list .str-chat__message-attachment--image, .str-chat__attachment-list .str-chat__message-attachment-card--header, .str-chat__attachment-list .str-chat__message-attachment--video':
        {
          borderRadius: '4px 4px 0px 0px',
        },
      '& .str-chat__message-attachment-audio-widget': {
        '& .str-chat__message-attachment-audio-widget--text-first-row': {
          '& .str-chat__message-attachment-file--item-download': {
            display: 'none',
          },
          '& .str-chat__message-attachment-audio-widget--title': {
            paddingRight: '30px',
            [theme.breakpoints.down('xs')]: {
              width: '100%',
              maxWidth: 200,
            },
          },
        },
      },

      '& .str-chat__message-attachment-file--item-text': {
        position: 'relative',
        '& .str-chat__message-attachment-file--item-download': {
          position: 'absolute',
          top: '75%',
          transform: 'translateY(-50%)',
          left: 64,
        },
      },

      '& .str-chat__message-attachment-file--item .str-chat__message-attachment-file--item-text .str-chat__message-attachment-file--item-first-row .str-chat__message-attachment-file--item-name':
        {
          paddingRight: '30px',
          [theme.breakpoints.down('xs')]: {
            width: '100%',
            maxWidth: 200,
          },
        },
      '& .str-chat__message-attachment-download-icon': {
        width: 20,
        height: 20,
      },

      '& .str-chat__list-notifications': {
        [theme.breakpoints.up('sm')]: {
          margin: 0,
        },
        '& .notification-warning': {
          display: 'none',
        },
      },
      '& .str-chat__message-input .str-chat__file-input-container': {
        color: theme.palette.primary.main,
        borderRight: `1px solid ${DividersAndCardBorders}`,
        width: '41px !important',
        height: '35px !important',
        paddingRight: theme.spacing(1),
        paddingTop: '1px',
        paddingLeft: '3px',
        [theme.breakpoints.down('xs')]: {
          paddingBottom: '2px',
          width: '43px !important',
          paddingLeft: '2px',
          paddingTop: 0,
        },
        borderRadius: 0,
        '& svg': {
          opacity: 1,
          fontSize: '20px',
        },
      },
    },
  }),
);
